/**
 * A porta entre o financeiro gerencial (módulo 15; 0102) e o servidor: a DRE,
 * os lançamentos gerenciais, o plano de contas, os títulos e as três
 * gravações do plano. A mesa e o celular usam a mesma.
 */
import { comCorpo, pedir } from '@/lib/pedido';

export type Grupo = 'receita' | 'custo_direto' | 'custo_indireto' | 'depreciacao';
export type Recorte = 'empresa' | 'contrato' | 'cliente' | 'obra' | 'centro' | 'equipamento';
export type Origem =
  | 'receita.medicao'
  | 'custo.combustivel'
  | 'custo.pecas'
  | 'custo.terceiros'
  | 'custo.mao_de_obra_oficina'
  | 'custo.mao_de_obra_operacao'
  | 'custo.indireto'
  | 'custo.depreciacao';

export interface TotaisDaDre {
  readonly receita: number;
  readonly custo_direto: number;
  readonly custo_indireto: number;
  readonly depreciacao: number;
  readonly margemDeContribuicao: number;
  readonly resultado: number;
  readonly margem: number | null;
  readonly margemDeContribuicaoPercentual: number | null;
}

export interface LinhaDaDre {
  readonly contaId: string | null;
  readonly codigo: string | null;
  readonly nome: string;
  readonly contaContabil: string | null;
  readonly valor: number;
  readonly anterior: number;
  readonly origens: readonly Origem[];
}

export interface GrupoDaDre {
  readonly grupo: Grupo;
  readonly rotulo: string;
  readonly valor: number;
  readonly anterior: number;
  readonly linhas: readonly LinhaDaDre[];
}

export interface ChaveDoRecorte extends Partial<TotaisDaDre> {
  readonly chave: string | null;
  readonly balde: 'sem_vinculo' | 'sem_base' | null;
  readonly codigo: string | null;
  readonly nome: string;
  readonly receita: number;
}

export interface MesDaDre extends Partial<TotaisDaDre> {
  readonly competencia: string;
  readonly receita: number;
}

export interface Dre {
  readonly periodo: { readonly de: string; readonly ate: string; readonly competencias: readonly string[] };
  readonly anterior: { readonly de: string; readonly ate: string; readonly competencias: readonly string[] };
  readonly recorte: Recorte;
  readonly chave: { readonly id: string; readonly codigo: string | null; readonly nome: string } | null;
  /** 0098: quem tem escopo vê só a receita — sem custo e sem resultado. */
  readonly soReceita: boolean;
  readonly demonstrativo: readonly GrupoDaDre[];
  readonly resultado: { readonly atual: Partial<TotaisDaDre> & { readonly receita: number }; readonly anterior: Partial<TotaisDaDre> & { readonly receita: number } };
  readonly porChave: readonly ChaveDoRecorte[];
  readonly mensal: readonly MesDaDre[];
  readonly conferencia: {
    readonly receita: { readonly dre: number; readonly origem: number };
    readonly custoTotal: { readonly dre: number; readonly origem: number } | null;
    readonly fecha: boolean;
  };
  readonly premissas: { readonly inclui: readonly string[]; readonly naoInclui: readonly string[]; readonly completo: boolean };
  readonly escrituracao: string;
}

export interface LancamentoGerencial {
  readonly competencia: string;
  readonly contaCodigo: string | null;
  readonly contaNome: string;
  readonly contaContabil: string | null;
  readonly grupo: Grupo | null;
  readonly equipamento: string | null;
  readonly contrato: string | null;
  readonly valor: number;
}

export interface ContaDoPlano {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly grupo: Grupo;
  readonly contaContabil: string | null;
  readonly ativo: boolean;
  readonly atualizadoEm: string;
  readonly atualizadoPor: string | null;
  /** A versao da conta (backend/src/http/versao-da-ficha.ts): o formulario a devolve no `PUT`. */
  readonly versao?: string | null;
}

export interface ClassificacaoDoPlano {
  readonly id: string;
  readonly origem: Origem;
  readonly centroCustoId: string | null;
  readonly centroCodigo: string | null;
  readonly centroNome: string | null;
  readonly contaId: string;
  readonly contaCodigo: string;
  readonly contaNome: string;
}

export interface DescricaoDaOrigem {
  readonly origem: Origem;
  readonly grupo: Grupo;
  readonly rotulo: string;
  readonly modulo: string;
  readonly deOndeVem: string;
}

export interface Plano {
  readonly contas: readonly ContaDoPlano[];
  readonly classificacoes: readonly ClassificacaoDoPlano[];
  readonly centros: readonly { readonly id: string; readonly codigo: string; readonly nome: string; readonly ativo: boolean }[];
  readonly origens: readonly DescricaoDaOrigem[];
  readonly grupos: readonly { readonly grupo: Grupo; readonly rotulo: string }[];
  readonly validacao: {
    readonly id: string;
    readonly validadoEm: string;
    readonly contador: string;
    readonly observacao: string;
    readonly registradoPor: string | null;
    readonly criadoEm: string;
    readonly mudancasDesde: readonly string[];
  } | null;
  readonly validacoesAnteriores: readonly { readonly id: string; readonly validadoEm: string; readonly contador: string }[];
  readonly escrituracao: string;
}

export type SituacaoDoTitulo = 'a_receber' | 'recebido_a_menor' | 'recebido';

export interface TituloAReceber {
  readonly id: string;
  readonly numero: string;
  readonly emitidoEm: string;
  readonly recebidoEm: string | null;
  readonly valorRecebido: number | null;
  readonly competencia: string;
  readonly contrato: string;
  readonly cliente: string;
  readonly valor: number;
  readonly emAberto: number;
  readonly situacao: SituacaoDoTitulo;
  /** 0108: a cobrança tem título da matriz ligado; conta pelo título. */
  readonly noTitulo?: boolean;
}

/** 0108: o título da matriz do módulo 04 (do ERP quando ele é o mestre), com o vencimento e a baixa. */
export interface TituloDaMatriz {
  readonly id: string;
  readonly tipo: 'a_pagar' | 'a_receber';
  readonly numero: string;
  readonly contraparte: string;
  readonly emitidoEm: string;
  readonly vencimento: string;
  readonly valor: number;
  readonly baixado: number;
  readonly emAberto: number;
  readonly ultimaBaixa: string | null;
  readonly origem: 'lote' | 'contingencia' | 'plataforma';
  readonly sistema: string | null;
  readonly pedido: string | null;
  readonly documento: string | null;
}

export interface MedicaoAFaturar {
  readonly medicaoId: string;
  readonly competencia: string;
  readonly contrato: string;
  readonly cliente: string;
  readonly valor: number;
  readonly aprovadaEm: string | null;
}

export interface FaturaAPagar {
  readonly id: string;
  readonly documento: string;
  readonly emitidaEm: string | null;
  readonly competencia: string | null;
  readonly valor: number;
  readonly situacao: 'aberta' | 'conciliada' | 'divergente';
  readonly fornecedor: string;
  readonly posto: string | null;
  readonly valorLancado: number;
  readonly diferenca: number;
}

export interface Titulos {
  readonly aReceber: readonly TituloAReceber[];
  readonly aFaturar: readonly MedicaoAFaturar[];
  readonly aPagar: readonly FaturaAPagar[];
  readonly titulosDaMatriz?: {
    readonly aReceber: readonly TituloDaMatriz[];
    readonly aPagar: readonly TituloDaMatriz[];
    readonly mestres: { readonly aPagar: string | null; readonly aReceber: string | null };
    readonly frase: string;
  };
  readonly totais: { readonly aReceberEmAberto: number; readonly aFaturar: number; readonly aPagarEmAberto: number; readonly titulosAPagarEmAberto?: number };
  readonly semLicenca: readonly string[];
  readonly frase: string;
}

export interface BuscaDaDre {
  readonly de: string;
  readonly ate: string;
  readonly recorte: Recorte;
  readonly chave?: string | undefined;
}

export interface NovaConta {
  readonly codigo: string;
  readonly nome: string;
  readonly grupo: Grupo;
  readonly contaContabil: string | null;
}

export interface ContaAlterada {
  readonly codigo: string;
  readonly nome: string;
  readonly contaContabil: string | null;
  readonly ativo: boolean;
  readonly motivo?: string;
  /** A versao de quando o formulario abriu: o servidor recusa se outra pessoa gravou depois. */
  readonly versao?: string | null;
}

export interface NovaClassificacao {
  readonly origem: Origem;
  readonly centroCustoId: string | null;
  readonly contaId: string | null;
  readonly motivo: string;
}

export interface NovaValidacao {
  readonly validadoEm: string;
  readonly contador: string;
  readonly observacao?: string;
}

export const apiDoFinanceiro = {
  dre: (b: BuscaDaDre) =>
    pedir<Dre>(
      `/financeiro/dre?de=${b.de.slice(0, 7)}&ate=${b.ate.slice(0, 7)}&recorte=${b.recorte}${b.chave ? `&chave=${encodeURIComponent(b.chave)}` : ''}`,
    ),
  lancamentos: (competencia: string) =>
    pedir<{ competencia: string; lancamentos: readonly LancamentoGerencial[]; escrituracao: string }>(`/financeiro/lancamentos?competencia=${competencia.slice(0, 7)}`),
  plano: () => pedir<Plano>('/financeiro/plano'),
  titulos: () => pedir<Titulos>('/financeiro/titulos'),

  criarConta: (c: NovaConta) =>
    comCorpo<{ id: string; codigo: string; nome: string; grupo: Grupo }>('POST', '/financeiro/contas', {
      codigo: c.codigo.trim(),
      nome: c.nome.trim(),
      grupo: c.grupo,
      contaContabil: c.contaContabil?.trim() ? c.contaContabil.trim() : null,
    }),
  alterarConta: (id: string, c: ContaAlterada) =>
    pedir<{ id: string; codigo: string; nome: string; ativo: boolean; antes: { ativo: boolean; codigo: string } }>(`/financeiro/contas/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({
        codigo: c.codigo.trim(),
        nome: c.nome.trim(),
        contaContabil: c.contaContabil?.trim() ? c.contaContabil.trim() : null,
        ativo: c.ativo,
        ...(c.motivo?.trim() ? { motivo: c.motivo.trim() } : {}),
        ...(c.versao ? { versao: c.versao } : {}),
      }),
    }),
  classificar: (k: NovaClassificacao) =>
    pedir<{ id: string | null; origem: Origem; antes: string | null; depois: { codigo: string; nome: string } | null }>('/financeiro/classificacao', {
      method: 'PUT',
      body: JSON.stringify({ origem: k.origem, centroCustoId: k.centroCustoId, contaId: k.contaId, motivo: k.motivo.trim() }),
    }),
  validar: (v: NovaValidacao) =>
    comCorpo<{ id: string; validadoEm: string; contador: string; contas: number }>('POST', '/financeiro/validacoes', {
      validadoEm: v.validadoEm,
      contador: v.contador.trim(),
      ...(v.observacao?.trim() ? { observacao: v.observacao.trim() } : {}),
    }),
};
